/**
 * Language Router & Persistence System - PublikaYA
 * =================================================
 * Funciones:
 * 1. Redirige al idioma preferido del usuario al cargar la página
 * 2. Reescribe todos los links internos para mantener el idioma
 * 3. Cae a español cuando una página no está traducida
 * 4. Persiste la preferencia de idioma en localStorage
 *
 * Orden de ejecución:
 *   Fase 1 (síncrona): Redirigir al idioma preferido ANTES de renderizar
 *   Fase 2 (DOMContentLoaded): Reescribir links internos
 *   Fase 3 (load): Persistir idioma actual + re-reescritura final
 */

(function () {
    'use strict';

    /* ============================================
       CONFIGURACIÓN
       ============================================ */
    var SUPPORTED_LANGS = ['es', 'en', 'fr', 'de'];
    var STORAGE_KEY = 'publishka_user_language_preference';

    // Páginas estáticas que SÍ tienen traducción
    var TRANSLATED_STATIC = [
        'index.html',
        'categories.html',
        'diseno-web-colombia.html',
        'login.html',
        'registro.html',
        'registro-negocio.html',
        'blog.html',
        'blog-cocina.html',
        'blog-esoterico.html',
        'blog-tecnologia.html',
        'blog-gaming.html',
        'blog-bodas.html',
        'blog-mascotas.html',
        'search.html',
        'terminos.html',
        'privacidad.html',
        'acerca-de.html',
        'ayuda.html',
        'beneficios.html',
        'contacto.html',
        'medellin.html',
        'ocana.html'
    ];

    // Páginas que NO están traducidas (compartidas entre idiomas)
    var NON_TRANSLATED = [
        'acerca-de-nosotros.html',
        'user-dashboard.html',
        'user-settings.html',
        'user-profile.html',
        'user-notifications.html',
        'bucaramanga.html',
        'tunja.html',
        '404.html'
    ];

    // Secciones completas sin traducción (se sirven solo en español, sin prefijo /en/, /fr/, /de/)
    var NON_TRANSLATED_PREFIXES = [
        '/juegos',
        '/creadores'
    ];

    // Categorías de blog con patrón 1: /blog-gaming/{slug}/{lang}/index.html
    // El slug es el mismo en todos los idiomas
    var BLOG_PATTERN_1 = ['blog-gaming', 'blog-tecnologia', 'blog-esoterico'];

    // Categorías de blog con patrón 2: /blog-cocina/{lang}/{slug-trad}/index.html
    // El slug CAMBIA por idioma → no se puede autoconvertir
    var BLOG_PATTERN_2 = ['blog-cocina', 'blog-bodas', 'blog-mascotas', 'blog'];

    // Set para búsqueda rápida
    var translatedSet = {};
    var nonTranslatedSet = {};
    for (var t = 0; t < TRANSLATED_STATIC.length; t++) { translatedSet[TRANSLATED_STATIC[t]] = true; }
    for (var n = 0; n < NON_TRANSLATED.length; n++) { nonTranslatedSet[NON_TRANSLATED[n]] = true; }

    /* ============================================
       UTILIDADES
       ============================================ */

    /** Detecta el idioma desde la URL */
    function detectCurrentLanguage() {
        var path = window.location.pathname;
        // Prefijo (Pattern 2: /en/blog-cocina/...)
        if (path.indexOf('/en/') === 0) return 'en';
        if (path.indexOf('/fr/') === 0) return 'fr';
        if (path.indexOf('/de/') === 0) return 'de';
        // Sufijo (Pattern 1: /blog-gaming/{slug}/en/)
        var m = path.match(/\/(blog-gaming|blog-tecnologia|blog-esoterico)\/[^\/]+\/(en|fr|de)\/$/);
        if (m) return m[2];
        // Prefijo (Pattern 1 también: /blog-esoterico/en/{slug}/)
        var m2 = path.match(/\/(blog-gaming|blog-tecnologia|blog-esoterico)\/(en|fr|de)\/[^\/]+\/$/);
        if (m2) return m2[2];
        return 'es';
    }

    /** Obtiene el prefijo de URL para un idioma */
    function getLanguagePrefix(lang) {
        return lang === 'es' ? '/' : '/' + lang + '/';
    }

    /** Lee preferencia guardada */
    function getUserPreference() {
        try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
    }

    /** Guarda preferencia */
    function saveUserPreference(lang) {
        try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) {}
    }

    /** Normaliza una ruta: elimina prefijo de idioma, trailing slash, etc. */
    function normalizePath(path) {
        var p = path;
        // Quitar prefijo de idioma existente
        p = p.replace(/^\/(en|fr|de)(\/|$)/, '/');
        // Quitar trailing slash pero NO para root
        if (p.length > 1 && p.charAt(p.length - 1) === '/') {
            p = p.slice(0, -1);
        }
        return p || '/';
    }

    /** Extrae el nombre de archivo de una ruta */
    function getFilename(path) {
        var parts = path.split('/');
        return parts[parts.length - 1] || '';
    }

    /** Determina si una ruta corresponde a una página traducida */
    function isTranslatedPath(path) {
        var normalized = normalizePath(path);
        var filename = getFilename(normalized);
        if (translatedSet[filename]) return true;

        // Blog posts Pattern 1 (slug compartido entre idiomas)
        for (var i = 0; i < BLOG_PATTERN_1.length; i++) {
            if (normalized.indexOf('/' + BLOG_PATTERN_1[i] + '/') === 0) {
                return true;
            }
        }

        // Blog posts Pattern 2 (slug diferente por idioma → existe traducción pero no sabemos la URL)
        for (var j = 0; j < BLOG_PATTERN_2.length; j++) {
            if (normalized.indexOf('/' + BLOG_PATTERN_2[j] + '/') === 0) {
                return true;
            }
        }

        return false;
    }

    /** Determina si una ruta NO está traducida */
    function isNonTranslatedPath(path) {
        var normalized = normalizePath(path);
        for (var i = 0; i < NON_TRANSLATED_PREFIXES.length; i++) {
            var prefix = NON_TRANSLATED_PREFIXES[i];
            if (normalized === prefix || normalized.indexOf(prefix + '/') === 0) return true;
        }
        var filename = getFilename(normalized);
        return !!nonTranslatedSet[filename];
    }

    /** Convierte una ruta al idioma destino. Retorna null si no se puede convertir */
    function convertPathToLang(pathname, targetLang) {
        if (targetLang === 'es') {
            // Quitar prefijo de idioma → español
            return normalizePath(pathname) || '/';
        }

        // Ya está en el idioma destino
        if (pathname.indexOf('/' + targetLang + '/') === 0) return null;

        // Root
        var normalized = normalizePath(pathname);

        // Páginas no traducidas → no redirigir
        if (isNonTranslatedPath(normalized)) return null;

        // Páginas traducidas estáticas
        var filename = getFilename(normalized);
        if (translatedSet[filename]) {
            if (normalized === '/') return '/' + targetLang + '/';
            return '/' + targetLang + normalized;
        }

        // Blog Pattern 1: /blog-gaming/{slug}/ → /blog-gaming/{targetLang}/{slug}/
        for (var i = 0; i < BLOG_PATTERN_1.length; i++) {
            var prefix = '/' + BLOG_PATTERN_1[i] + '/';
            if (normalized.indexOf(prefix) === 0) {
                var slug = normalized.slice(prefix.length);
                if (slug) {
                    // Quitar prefijo de idioma existente (ej: "en/pesadillas-transformacion" → "pesadillas-transformacion")
                    slug = slug.replace(/^(en|fr|de)\/?/, '');
                    if (!slug) return null;
                    return '/' + BLOG_PATTERN_1[i] + '/' + targetLang + '/' + slug + '/';
                }
            }
        }

        // Blog Pattern 2: slug diferente por idioma → no podemos autoconvertir
        for (var j = 0; j < BLOG_PATTERN_2.length; j++) {
            if (normalized.indexOf('/' + BLOG_PATTERN_2[j] + '/') === 0) {
                return null; // No redirigir, el usuario que haga clic en links localizados manualmente
            }
        }

        // Para cualquier otra ruta, intentar prefijar
        return '/' + targetLang + normalized;
    }

    /* ============================================
       FASE 1: REDIRECCIÓN AL IDIOMA PREFERIDO
       ============================================ */

    function redirectToPreferredLanguage() {
        var preference = getUserPreference();
        if (!preference || preference === 'es') return false;

        // Nunca redirigir desde la raíz: el usuario que está en / quiere español
        if (window.location.pathname === '/' || window.location.pathname === '') {
            return false;
        }

        // Si este redirect ya falló antes (404), no redirigir de nuevo (evita bucle)
        try {
            if (sessionStorage.getItem('pk_lang_404_' + preference)) {
                sessionStorage.removeItem('pk_lang_404_' + preference);
                return false;
            }
        } catch (_) {}

        var currentLang = detectCurrentLanguage();
        if (currentLang === preference) return false; // Ya está correcto

        var targetPath = convertPathToLang(window.location.pathname, preference);
        if (!targetPath || targetPath === window.location.pathname) return false;

        var targetUrl = window.location.origin + targetPath +
            window.location.search + window.location.hash;

        // Solo redirigir si la URL cambia realmente
        if (targetUrl !== window.location.href) {
            window.location.href = targetUrl;
            return true;
        }
        return false;
    }

    /* ============================================
       FASE 2: REESCRITURA DE LINKS
       ============================================ */

    function rewriteLinks() {
        var currentLang = detectCurrentLanguage();
        if (currentLang === 'es') {
            saveUserPreference('es');
            return;
        }

        var langPrefix = getLanguagePrefix(currentLang);

        var allLinks = document.querySelectorAll('a[href]');
        for (var i = 0; i < allLinks.length; i++) {
            var link = allLinks[i];
            var href = link.getAttribute('href');
            if (!href) continue;

            // 1. Saltar enlaces externos, anclas, protocolos especiales
            if (href.indexOf('http') === 0 ||
                href.indexOf('#') === 0 ||
                href.indexOf('mailto:') === 0 ||
                href.indexOf('tel:') === 0 ||
                href.indexOf('javascript:') === 0 ||
                href.indexOf('//') === 0) continue;

            // 2. Saltar enlaces que YA tienen prefijo de idioma
            if (href.indexOf('/en/') === 0 ||
                href.indexOf('/fr/') === 0 ||
                href.indexOf('/de/') === 0) continue;

            // 2.5 Saltar enlaces del LANGUAGE SWITCHER (ya tienen las URLs correctas)
            if (link.closest && link.closest('.language-switcher, .lang-dropdown')) continue;
            if (link.classList && link.classList.contains('lang-option')) continue;

            // 3. Saltar rutas absolutas externas (con dominio)
            if (href.indexOf('//') > 0) continue;

            // 4. Determinar la ruta normalizada
            var pathLower = href.toLowerCase();
            var absolutePath = href.charAt(0) === '/' ? href : '/' + href;
            var normalized = normalizePath(absolutePath);

            // 5. Saltar páginas no traducidas
            if (isNonTranslatedPath(normalized)) continue;

            // 6. Blog Pattern 2: no autoconvertir slugs
            var isPattern2 = false;
            for (var j = 0; j < BLOG_PATTERN_2.length; j++) {
                if (normalized.indexOf('/' + BLOG_PATTERN_2[j] + '/') === 0) {
                    isPattern2 = true;
                    break;
                }
            }
            if (isPattern2) continue;

            // 7. Construir nuevo href
            var newHref;

            if (normalized === '/' || normalized === '') {
                newHref = langPrefix;
            }
            else if (href.charAt(0) === '/') {
                newHref = langPrefix + normalized.slice(1);
            }
            else {
                newHref = langPrefix + href;
            }

            // 8. Aplicar solo si cambió
            if (newHref && newHref !== href) {
                link.setAttribute('href', newHref);
            }
        }

        saveUserPreference(currentLang);
    }

    /* ============================================
       FASE 2.5: PARCHADO A PRUEBA DE BALAS
       Agrega onclick inline a los links del selector de idioma
       para guardar la preferencia incluso si los event listeners fallan
       ============================================ */

    function patchLangSwitcherLinks() {
        var langOptions = document.querySelectorAll('.language-switcher .lang-option, .lang-switcher a[href]');
        if (!langOptions || !langOptions.length) return;

        for (var i = 0; i < langOptions.length; i++) {
            var link = langOptions[i];
            
            // Saltar si ya fue procesado
            if (link.getAttribute('data-lang-patched')) continue;
            
            var href = link.getAttribute('href') || '';

            // Determinar el código de idioma
            var langCode = 'es';
            if (href === '/') langCode = 'es';
            else if (href.indexOf('/en') === 0) langCode = 'en';
            else if (href.indexOf('/fr') === 0) langCode = 'fr';
            else if (href.indexOf('/de') === 0) langCode = 'de';
            else continue;

            // Marcar como procesado ANTES de modificar
            link.setAttribute('data-lang-patched', langCode);
            
            // Guardar preferencia ANTES de navegar — usando setAttribute que incrusta
            // el valor de langCode en este momento (sin closure, sin race conditions)
            link.setAttribute('onclick',
                "try{localStorage.setItem('publishka_user_language_preference','" + langCode + "');}catch(e){} return true;");
        }
    }

    /* ============================================
       INICIALIZACIÓN
       ============================================ */

    function init() {
        // Fase 0: Parchear links del switcher INMEDIATAMENTE (antes que cualquier otro script)
        function patchNow() {
            try { patchLangSwitcherLinks(); } catch(_) {}
        }
        patchNow();

        // Fase 1: Redirigir inmediatamente
        var redirected = redirectToPreferredLanguage();
        if (redirected) return;

        // Fase 2: Cuando el DOM esté listo
        function onDOMReady() {
            try { patchLangSwitcherLinks(); } catch(_) {}
            try { rewriteLinks(); } catch(_) {}
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', onDOMReady);
        } else {
            onDOMReady();
        }

        // Fase 3: Al cargar completamente
        window.addEventListener('load', function () {
            var currentLang = detectCurrentLanguage();
            saveUserPreference(currentLang);
            setTimeout(function () {
                try { patchLangSwitcherLinks(); } catch(_) {}
                try { rewriteLinks(); } catch(_) {}
            }, 200);
            
            // Fase 4: Re-parchear más tarde (por si otros scripts modificaron el DOM)
            setTimeout(function () {
                try { patchLangSwitcherLinks(); } catch(_) {}
            }, 1000);
        });
    }

    // Ejecutar
    init();

    // Exportar API pública
    window.LanguagePersistence = {
        detectCurrentLanguage: detectCurrentLanguage,
        getLanguagePrefix: getLanguagePrefix,
        getUserPreference: getUserPreference,
        rewriteLinks: rewriteLinks,
        convertPathToLang: convertPathToLang
    };

})();
